"use client"

import { Suspense, useEffect } from "react"
import { useRouter } from "next/navigation"

import { ApolloPageShell, ApolloPageBandProvider } from "@/components/ui/apollo-page"
import { Skeleton } from "@/components/ui/skeleton"

import { CheckinWidget } from "@/components/hr/checkin-widget"
import { AdminDashboard } from "@/components/dashboard/variants/admin-dashboard"
import { TeamLeaderDashboard } from "@/components/dashboard/variants/team-leader-dashboard"
import { CoordinatorDashboard } from "@/components/dashboard/variants/coordinator-dashboard"
import { AccountantDashboard } from "@/components/dashboard/variants/accountant-dashboard"
import { AccountantConfirmationsDashboard } from "@/components/dashboard/variants/accountant-confirmations-dashboard"
import type { FeatureFlags } from "@/types"
import { DoctorDashboard } from "@/components/dashboard/variants/doctor-dashboard"
import { AgentDashboard } from "@/components/dashboard/variants/agent-dashboard"
import { FrontdeskDashboard } from "@/components/dashboard/variants/frontdesk-dashboard"
import { GenericDashboard } from "@/components/dashboard/variants/generic-dashboard"

import { isFrontdeskCalendarHome, resolveDashboardRole } from "@/lib/dashboard-role"
import { hasFlag } from "@/lib/feature-flags"
import { hasRole, ROLES } from "@/lib/roles"
import { useAuthStore } from "@/stores/auth-store"

/**
 * Skeleton shown while `/auth/me` is still in flight. Mirrors the shipped
 * dashboard's band structure (header → stats strip → content grid) so the
 * layout does not jump when the real variant mounts.
 */
function DashboardSkeleton() {
  return (
    <ApolloPageShell>
      <div className="shrink-0 border-b border-border px-4 py-5 md:px-6">
        <Skeleton className="mb-2 h-3 w-24" />
        <Skeleton className="h-9 w-56" />
        <div className="mt-2.5 flex items-baseline gap-7">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-[13px] w-24" />
          ))}
        </div>
      </div>

      <div className="scrollbar-thin min-h-0 flex-1 overflow-auto">
        <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
          <Skeleton className="h-[220px] w-full rounded-lg" />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-[280px] w-full rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    </ApolloPageShell>
  )
}

/**
 * Dashboard dispatcher.
 *
 * Thin by design: it resolves ONE canonical dashboard role from the auth
 * store's `user.roles` (see {@link resolveDashboardRole}) and hands off to the
 * matching variant under `components/dashboard/variants/`. All data fetching,
 * permission gating and layout live inside the variants — nothing here knows
 * about widgets.
 *
 * ONE variant escapes that walk: `FrontdeskDashboard` is selected by the
 * `frontdesk_dashboard_enabled` flag rather than by a PRIORITY entry, because
 * `frontdesk` is a live role on four clinics but only one of them wants the new
 * board. See `frontdesk-dashboard.tsx` for why that split is deliberate.
 *
 * While `user === null` (auth `/me` unresolved) a shell-shaped skeleton renders
 * rather than a variant, which also keeps the hide-during-hydration contract of
 * `usePermissions()` intact: no variant mounts before its grants are known.
 *
 * The one thing the dispatcher does own is the HR check-in band: it is
 * cross-cutting (every role punches in), so it is declared ONCE here and handed
 * to the variant's shell through {@link ApolloPageBandProvider} rather than
 * duplicated into seven variants. `CheckinWidget` self-gates on the
 * `hr_attendance_enabled` flag AND `hr.attendance.self`, so it stays inert on
 * clinics without the HR attendance module. It is ALSO gated (260724) behind
 * `hr_self_checkin_enabled`, a frontend-only flag never sent by the backend —
 * a deliberate temporary hide of the punch-in/out band with no server-side
 * change. Either way `ApolloPageBandProvider`'s `band` is rendered bare (no
 * wrapper), so a self-gated `null` leaves nothing behind here.
 */
export default function DashboardPage() {
  const user = useAuthStore((state) => state.user)
  const router = useRouter()

  // Simple clinic mode: roles without a purpose-built variant (frontdesk,
  // manager — resolveDashboardRole falls through to "generic") land on the
  // day's chair grid instead of the CRM sales dashboard. The calendar IS the
  // front desk's workspace; roles with a clinic-relevant variant (doctor,
  // admin, coordinator, accountant) keep their dashboards. This still holds
  // for frontdesk_dashboard_enabled clinics: that flag is read below, after
  // this redirect, so a simple-mode front desk keeps the calendar.
  const simpleClinicMode = user?.feature_flags?.simple_clinic_mode ?? false
  // Radiance 261007: the same landing for the front desk alone, on a clinic
  // whose Needs booking board stays on for its coordinators — so the board's
  // own flag cannot be the switch. Keyed like the board below (frontdesk AND
  // no dashboard of their own), and checked first, so it wins over the board.
  const redirectToCalendar =
    user !== null &&
    ((simpleClinicMode && resolveDashboardRole(user.roles) === "generic") ||
      isFrontdeskCalendarHome(user))

  // Affiliators have one screen, so `/` is a doorway rather than a dashboard.
  // They are deliberately absent from PRIORITY (dashboard-role.ts) — there is
  // no partner dashboard and GenericDashboard would render them a header over
  // an empty page — so this redirect, not a variant, is what lands them
  // somewhere useful. Checked before the simple-clinic branch: a partner's
  // destination does not depend on how the clinic runs its own front desk.
  const redirectToAffiliator = user !== null && hasRole(user.roles, ROLES.AFFILIATOR)

  // Drivers, same reasoning as the affiliator above and the same fix: `driver`
  // is deliberately absent from PRIORITY, so GenericDashboard would hand them a
  // header over an empty page. Their whole app is the runs screen, which lives
  // in the mobile tree because that is where the job is done — a driver reads
  // it at a kerb, not at a desk — so `/` is a doorway to it rather than a
  // dashboard. Gated on the PERMISSION, not the slug, so a clinic's own driver
  // role lands in the same place.
  // Gated on "holds the driving permission AND has no dashboard of their own",
  // not on the slug: admin, coordinator and team_leader also hold
  // `logistics.drive` (or can be granted it) but each has a real dashboard, and
  // resolveDashboardRole is exactly the question "does this user have one?".
  // A clinic that invents its own driver role therefore lands here too.
  // The filing roles — photographer, radiographer — land on the Clients list.
  // Same reasoning as the two above: neither is in PRIORITY (dashboard-role.ts),
  // so GenericDashboard would hand them a header over an empty page. Their whole
  // app is a patient's files rail, reached from Clients.
  //
  // Keyed on the role plus "has no dashboard of their own", matching the driver
  // rule below: `leads.photos.upload` alone would also catch an admin granted it
  // to file one picture, and admins have a real dashboard to land on.
  const redirectToClients =
    user !== null &&
    (hasRole(user.roles, ROLES.MARKETING) || hasRole(user.roles, ROLES.RADIOGRAPHER)) &&
    resolveDashboardRole(user.roles) === "generic"

  const redirectToRuns =
    user !== null &&
    user.permissions.includes("logistics.drive") &&
    resolveDashboardRole(user.roles) === "generic"

  // Front desk (Dentour 260818) is the one variant NOT selected by the role
  // walk — `frontdesk` stays absent from PRIORITY so it keeps resolving to
  // "generic", which is what leaves both the redirect above and the other three
  // `frontdesk` clinics (Dentex, Gjergji, Zaffiro) on their existing board.
  // The flag, not the role, is the switch. Gated behind the redirect below, so
  // a simple-mode clinic still lands on the calendar.
  const showFrontdeskDashboard =
    user !== null &&
    hasRole(user.roles, ROLES.FRONTDESK) &&
    resolveDashboardRole(user.roles) === "generic" &&
    hasFlag(user.feature_flags, "frontdesk_dashboard_enabled")

  useEffect(() => {
    if (redirectToAffiliator) {
      router.replace("/affiliator/patients")
      return
    }
    if (redirectToRuns) {
      router.replace("/logistics/runs")
      return
    }
    if (redirectToClients) {
      router.replace("/clients")
      return
    }
    if (redirectToCalendar) router.replace("/calendar")
  }, [redirectToAffiliator, redirectToRuns, redirectToClients, redirectToCalendar, router])

  if (
    user === null ||
    redirectToAffiliator ||
    redirectToRuns ||
    redirectToClients ||
    redirectToCalendar
  ) {
    return <DashboardSkeleton />
  }

  return (
    <ApolloPageBandProvider
      band={<CheckinWidget className="mx-4 mt-4 shrink-0 md:mx-6 md:mt-6" />}
    >
      {showFrontdeskDashboard ? <FrontdeskDashboard /> : renderDashboard(user.roles, user.feature_flags)}
    </ApolloPageBandProvider>
  )
}

/** Canonical role → variant. Split out so the dispatcher body stays flat. */
function renderDashboard(roles: string[] | undefined | null, flags: FeatureFlags | undefined | null) {
  switch (resolveDashboardRole(roles)) {
    case "admin":
      return <AdminDashboard />
    case "team_leader":
      return <TeamLeaderDashboard />
    case "coordinator":
      // The desk keeps its tab in ?tab= (ArtDent 261027); useSearchParams
      // needs a Suspense boundary on a prerendered route.
      return (
        <Suspense fallback={<DashboardSkeleton />}>
          <CoordinatorDashboard />
        </Suspense>
      )
    case "accountant":
      // ArtDent 260930 — an accountant whose job is confirming card and bank
      // money gets the confirmations board, not the invoicing dashboard. The
      // admin case above already caught an owner wearing both hats.
      return flags?.accountant_confirmations_focus_enabled
        ? <AccountantConfirmationsDashboard />
        : <AccountantDashboard />
    case "doctor":
      return <DoctorDashboard />
    case "agent":
      return <AgentDashboard />
    case "generic":
    default:
      return <GenericDashboard />
  }
}
